import React, { FC, ReactNode, useMemo, useState } from 'react';
import translate from '@translate';
import DownloadIcon from '@mui/icons-material/Download';
import {
    Box,
    Button,
    IconButton,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import moment from 'moment';
import FileDisplay from 'src/components/FileDisplay';
import { downloadFile } from 'src/components/FileUpload/functions';
import { isArchiveMode } from 'src/utils/archiveMode';
import { CustomFileActions } from 'src/components/FileUpload';
import Tooltip from 'src/components/Tooltip';
import { EyeIcon, TrashIcon } from 'src/components/SkladonIcon/SmallIcons';
import { colors } from 'src/themes/default';
import { FileType } from 'src/types/file';
import { getDateTimeFormat } from 'src/utils/timeConstants';

type CreatorRoleFilterValue = 'all' | 'skladon_employee' | 'client';
type SortColumn = 'file' | 'description' | 'type' | 'uploadedAt' | 'uploadedBy';
type SortDirection = 'asc' | 'desc';

const creatorRoleLabel = (role: string | null | undefined) => {
    if (!role) {
        return '';
    }
    const key = `file.creatorRole.${role}` as const;
    const t = translate(key);
    return t === key ? role : t;
};

export const fileDisplayName = (file: FileType) =>
    String(file.name || file.fileName || '');

const descriptionSortValue = (file: FileType) => String(file.description ?? '');
const typeSortValue = (file: FileType) => String(file.type ?? '');
const uploadedBySortValue = (file: FileType) => creatorRoleLabel(file.creatorRole);
const uploadedAtSortValue = (file: FileType) => {
    if (!file.createdAt) {
        return 0;
    }
    const ms = new Date(file.createdAt).getTime();
    return Number.isNaN(ms) ? 0 : ms;
};

const formatUploadedAt = (value: FileType['createdAt']) => {
    if (!value) {
        return '—';
    }
    const m = moment(value);
    return m.isValid() ? m.format(getDateTimeFormat()) : '—';
};

export interface EntityFilesTablePropsType {
    files?: FileType[];
    disabled: boolean;
    onDeleteFile: (file: FileType) => void;
    skladonUser?: number;
    /** Receiving-only editable description column. */
    showDescription?: boolean;
    onDescriptionChange?: (file: FileType, description: string) => void;
    descriptionMaxLength?: number;
    /** When set, shows a Type column with these actions (order/product selects). */
    customFileActions?: CustomFileActions;
    testIdPrefix?: string;
    /** Optional cell renderer for type column label when not using customFileActions. */
    typeColumnHeader?: ReactNode;
}

const EntityFilesTable: FC<EntityFilesTablePropsType> = (props) => {
    const theme = useTheme();
    const mobile = useMediaQuery(theme.breakpoints.down('sm'));
    const testIdPrefix = props.testIdPrefix || 'entity-files';
    const [roleFilter, setRoleFilter] = useState<CreatorRoleFilterValue>('all');
    const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [showFile, setShowFile] = useState<FileType | null>(null);

    const files = props.files || [];
    const showType = Boolean(props.customFileActions);

    const visibleFiles = useMemo(() => {
        const filtered =
            roleFilter === 'all' ? files : files.filter((f) => (f.creatorRole || '') === roleFilter);

        if (!sortColumn) {
            return filtered;
        }

        return [...filtered].sort((a, b) => {
            if (sortColumn === 'uploadedAt') {
                const cmp = uploadedAtSortValue(a) - uploadedAtSortValue(b);
                return sortDirection === 'asc' ? cmp : -cmp;
            }
            const getValue =
                sortColumn === 'file'
                    ? fileDisplayName
                    : sortColumn === 'description'
                      ? descriptionSortValue
                      : sortColumn === 'type'
                        ? typeSortValue
                        : uploadedBySortValue;
            const cmp = getValue(a).localeCompare(getValue(b), undefined, { sensitivity: 'base' });
            return sortDirection === 'asc' ? cmp : -cmp;
        });
    }, [files, roleFilter, sortColumn, sortDirection]);

    const handleSort = (column: SortColumn) => {
        if (sortColumn === column) {
            setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
            return;
        }
        setSortColumn(column);
        setSortDirection(column === 'uploadedAt' ? 'desc' : 'asc');
    };

    if (!files.length) {
        return null;
    }

    return (
        <Box sx={{ mt: 3, width: '100%' }} data-testid={`${testIdPrefix}-list`}>
            <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 720 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>
                                <TableSortLabel
                                    active={sortColumn === 'file'}
                                    direction={sortColumn === 'file' ? sortDirection : 'asc'}
                                    onClick={() => handleSort('file')}
                                    data-testid={`${testIdPrefix}-sort-file`}
                                >
                                    {translate('file.colFile')}
                                </TableSortLabel>
                            </TableCell>
                            {props.showDescription ? (
                                <TableCell sx={{ fontWeight: 600, minWidth: 280, width: '30%' }}>
                                    <TableSortLabel
                                        active={sortColumn === 'description'}
                                        direction={sortColumn === 'description' ? sortDirection : 'asc'}
                                        onClick={() => handleSort('description')}
                                        data-testid={`${testIdPrefix}-sort-description`}
                                    >
                                        {translate('file.colDescription')}
                                    </TableSortLabel>
                                </TableCell>
                            ) : null}
                            {showType ? (
                                <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>
                                    <TableSortLabel
                                        active={sortColumn === 'type'}
                                        direction={sortColumn === 'type' ? sortDirection : 'asc'}
                                        onClick={() => handleSort('type')}
                                        data-testid={`${testIdPrefix}-sort-type`}
                                    >
                                        {props.typeColumnHeader || translate('file.colType')}
                                    </TableSortLabel>
                                </TableCell>
                            ) : null}
                            <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', minWidth: 160 }}>
                                <TableSortLabel
                                    active={sortColumn === 'uploadedAt'}
                                    direction={sortColumn === 'uploadedAt' ? sortDirection : 'asc'}
                                    onClick={() => handleSort('uploadedAt')}
                                    data-testid={`${testIdPrefix}-sort-uploadedAt`}
                                >
                                    {translate('file.uploadedAt')}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', minWidth: 200 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        flexWrap: 'nowrap',
                                    }}
                                >
                                    <TableSortLabel
                                        active={sortColumn === 'uploadedBy'}
                                        direction={sortColumn === 'uploadedBy' ? sortDirection : 'asc'}
                                        onClick={() => handleSort('uploadedBy')}
                                        data-testid={`${testIdPrefix}-sort-uploadedBy`}
                                    >
                                        {translate('file.filterBy.creatorRole')}
                                    </TableSortLabel>
                                    <Select
                                        size="small"
                                        value={roleFilter}
                                        onChange={(e) =>
                                            setRoleFilter(e.target.value as CreatorRoleFilterValue)
                                        }
                                        onClick={(e) => e.stopPropagation()}
                                        data-testid={
                                            testIdPrefix === 'receiving-files'
                                                ? 'receiving-file-creator-role-filter'
                                                : `${testIdPrefix}-creator-role-filter`
                                        }
                                        sx={{ minWidth: 120 }}
                                        aria-label={translate('file.filterBy.creatorRole')}
                                    >
                                        <MenuItem value="all">
                                            {translate('file.filterBy.creatorRoleAll')}
                                        </MenuItem>
                                        <MenuItem value="skladon_employee">
                                            {translate('file.creatorRole.skladon_employee')}
                                        </MenuItem>
                                        <MenuItem value="client">
                                            {translate('file.creatorRole.client')}
                                        </MenuItem>
                                    </Select>
                                </Box>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap', width: 200 }}>
                                {translate('file.colActions')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visibleFiles.map((file, index) => {
                            const rowKey = file.id ?? `${file.fileName || ''}-${index}`;
                            const fileTitle = fileDisplayName(file);
                            const canEditDescription =
                                Boolean(props.showDescription) &&
                                !props.disabled &&
                                Boolean(props.onDescriptionChange);
                            const currentUserRole = props.skladonUser ? 'skladon_employee' : 'client';
                            const canDelete =
                                !props.disabled &&
                                (!file.creatorRole || file.creatorRole === currentUserRole);

                            return (
                                <TableRow
                                    key={rowKey}
                                    data-testid={`${testIdPrefix}-row-${file.id ?? index}`}
                                >
                                    <TableCell>
                                        <Typography variant="body2" noWrap title={fileTitle}>
                                            {fileTitle || '—'}
                                        </Typography>
                                    </TableCell>
                                    {props.showDescription ? (
                                        <TableCell>
                                            {canEditDescription ? (
                                                <TextField
                                                    key={`${rowKey}-desc`}
                                                    size="small"
                                                    fullWidth
                                                    hiddenLabel
                                                    variant="outlined"
                                                    placeholder={translate('file.description')}
                                                    aria-label={translate('file.description')}
                                                    defaultValue={file.description ?? ''}
                                                    inputProps={{
                                                        maxLength: props.descriptionMaxLength || 500,
                                                    }}
                                                    onBlur={(e) =>
                                                        props.onDescriptionChange?.(file, e.target.value)
                                                    }
                                                    sx={{ minWidth: { xs: 200, sm: 280 } }}
                                                />
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    {file.description != null && file.description !== ''
                                                        ? String(file.description)
                                                        : '—'}
                                                </Typography>
                                            )}
                                        </TableCell>
                                    ) : null}
                                    {showType && props.customFileActions ? (
                                        <TableCell>{props.customFileActions(file)}</TableCell>
                                    ) : null}
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary" noWrap>
                                            {formatUploadedAt(file.createdAt)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary" noWrap>
                                            {file.creatorRole ? creatorRoleLabel(file.creatorRole) : '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title={translate('file.download')}>
                                            <IconButton size="small" onClick={() => downloadFile(file, undefined, isArchiveMode())}>
                                                <DownloadIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={translate('file.display')}>
                                            <IconButton size="small" onClick={() => setShowFile(file)}>
                                                <EyeIcon />
                                            </IconButton>
                                        </Tooltip>
                                        {canDelete ? (
                                            mobile ? (
                                                <Tooltip title={translate('file.delete')}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => props.onDeleteFile(file)}
                                                        sx={{ '& svg path': { fill: colors.primary_O } }}
                                                    >
                                                        <TrashIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : (
                                                <Button size="small" onClick={() => props.onDeleteFile(file)}>
                                                    {translate('file.delete')}
                                                </Button>
                                            )
                                        ) : null}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            {showFile && <FileDisplay file={showFile} modal onClose={() => setShowFile(null)} />}
        </Box>
    );
};

export default EntityFilesTable;
